import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './InviteFriendsDialog.css';
import { usersAPI } from './services/api';
import { useTranslation } from 'react-i18next';

function InviteFriendsDialog({ isOpen, onClose, selectedFriends, onToggle, currentUser, excludeUserIds = [], footer }) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [friends, setFriends] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const overlayRef = useRef(null);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      if (!overlayRef.current) return;
      overlayRef.current.style.top = `${vv.offsetTop}px`;
      overlayRef.current.style.left = `${vv.offsetLeft}px`;
      overlayRef.current.style.width = `${vv.width}px`;
      overlayRef.current.style.height = `${vv.height}px`;
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const loadFriends = async () => {
      try {
        const companions = await usersAPI.getEventCompanions(currentUser?.id);
        setFriends(companions);
      } catch (error) {
        console.error('Error loading friends:', error);
        setFriends([]);
      }
    };

    const loadAllUsers = async () => {
      try {
        const users = await usersAPI.getAll();
        setAllUsers(users.filter(u => u.id !== currentUser?.id));
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };

    setSearchTerm('');
    loadFriends();
    loadAllUsers();
  }, [isOpen, currentUser?.id]);

  const filteredUsers = (() => {
    const excludeIds = new Set(excludeUserIds);

    if (!searchTerm.trim()) {
      return friends.filter(f => !excludeIds.has(f.userId));
    }

    const friendIds = new Set(friends.map(f => f.userId));
    const searchLower = searchTerm.toLowerCase();
    const matching = allUsers.filter(u =>
      !excludeIds.has(u.id) &&
      (u.displayName?.toLowerCase().includes(searchLower) ||
       u.username?.toLowerCase().includes(searchLower))
    );
    return [
      ...matching.filter(u => friendIds.has(u.id)),
      ...matching.filter(u => !friendIds.has(u.id)),
    ];
  })();

  const isSearching = searchTerm.trim().length > 0;

  if (!isOpen) return null;

  return createPortal(
    <div ref={overlayRef} className="friend-dialog-overlay" onClick={onClose}>
      <div className="friend-dialog" onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
        <div className="friend-dialog-header">
          <h3>{t('inviteFriendsDialog.title')}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <input
          type="text"
          placeholder={t('inviteFriendsDialog.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="friend-search"
        />
        <div className="friend-list">
          {filteredUsers.length === 0 ? (
            <p className="no-users">
              {searchTerm.trim()
                ? t('inviteFriendsDialog.noUsersFound')
                : t('inviteFriendsDialog.noFriends')}
            </p>
          ) : (
            filteredUsers.map(item => {
              const id = isSearching ? item.id : item.userId;
              const name = item.displayName || item.username;
              return (
                <div key={id} className="friend-item">
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedFriends.includes(id)}
                      onChange={() => onToggle(id)}
                    />
                    <span className="friend-name">{name}</span>
                  </label>
                </div>
              );
            })
          )}
        </div>
        {footer ?? (
          <button className="done-btn" onClick={onClose}>
            {t('common.done')}
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}

export default InviteFriendsDialog;